function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function toNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function toBool(v, fallback = false) {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

function normalizeKey(v) {
  return String(v || '')
    .trim()
    .toLowerCase();
}

function formatLimit(isUnlimited, value, t) {
  if (isUnlimited) return t('p.pricingPage.limits.unlimited', { defaultValue: 'Unlimited' });
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '0';
}

function getUltimatePlan(plans) {
  const list = safeArray(plans);
  return (
    list.find((p) => normalizeKey(p?.name) === 'ultimate') ||
    list.find((p) =>
      String(p?.name || '')
        .toLowerCase()
        .includes('ultimate')
    ) ||
    null
  );
}

// Feature keys that only gate parts of the bundle builder; never listed on the plan cards.
const INTERNAL_GATE_FEATURE_KEYS = new Set(['bundle-simple', 'bundle-set-options', 'bundle-collection-slots']);

function buildMasterFeaturesFromUltimate(ultimatePlan) {
  const fps = safeArray(ultimatePlan?.featurePlans);

  const raw = fps
    .map((fp) => fp?.feature)
    .filter(Boolean)
    .map((f) => ({
      id: String(f?.id || ''),
      name: String(f?.name || ''),
      featureKey: String(f?.key || ''),
      order: toNumber(f?.order, 0),
      isComingSoon: f?.isComingSoon === true,
    }))
    // Internal activation gates (Plans v6) are not merchant-facing plan features.
    .filter((x) => x.id && x.name && !INTERNAL_GATE_FEATURE_KEYS.has(x.featureKey));

  const byId = new Map();
  for (const f of raw) {
    const prev = byId.get(f.id);
    if (!prev) byId.set(f.id, f);
    else if (toNumber(f.order, 0) < toNumber(prev.order, 0)) byId.set(f.id, f);
  }

  const out = Array.from(byId.values());
  out.sort((a, b) => toNumber(a.order, 0) - toNumber(b.order, 0));
  return out;
}

function buildPlanFeatureIdSet(plan) {
  const fps = safeArray(plan?.featurePlans);
  const set = new Set();

  for (const fp of fps) {
    const featureId = String(fp?.feature?.id || '');
    if (featureId) set.add(featureId);
  }

  return set;
}

function isUltimatePlan(p) {
  const name = String(p?.name || '').toLowerCase();
  return name === 'ultimate' || name.includes('ultimate');
}

function buildUltimatePerks(planRaw, t) {
  const features = Array.isArray(planRaw?.features) ? planRaw.features : [];

  const cleaned = features.map((x) => String(x || '').trim()).filter(Boolean);

  if (cleaned.length) {
    return cleaned.map((raw) => {
      const isComingSoon = raw.endsWith(' [CS]');
      const withoutCs = isComingSoon ? raw.slice(0, -5) : raw;
      const pipeIdx = withoutCs.indexOf('|');
      const iconKey = pipeIdx >= 0 ? withoutCs.slice(0, pipeIdx) : null;
      const label = pipeIdx >= 0 ? withoutCs.slice(pipeIdx + 1) : withoutCs;
      return { label, isComingSoon, iconKey };
    });
  }

  return [
    {
      label: t('p.pricingPage.ultimate.perks.allUnlimited', {
        defaultValue: 'All app features with unlimited access',
      }),
      isComingSoon: false,
    },
    {
      label: t('p.pricingPage.ultimate.perks.prioritySupport', {
        defaultValue: 'Priority support',
      }),
      isComingSoon: false,
    },
    {
      label: t('p.pricingPage.ultimate.perks.vipServices', {
        defaultValue: 'VIP services',
      }),
      isComingSoon: false,
    },
  ];
}

export function createPlansAdaptor(t) {
  return function adaptPlansToUi(plansRaw) {
    // Retired plans (isActive=false, e.g. Basic) are only ever returned by the
    // API as the shop's OWN current plan. Keep that one so a legacy shop still
    // sees its current plan, but drop any other inactive plan so it is never
    // offered as something to choose.
    const raw = safeArray(plansRaw).filter(
      (p) => p && (p.isActive !== false || toBool(p?.isCurrentPlan, false))
    );

    const ultimate = getUltimatePlan(raw);
    const masterFeatures = buildMasterFeaturesFromUltimate(ultimate);

    const mapped = raw
      .map((p) => {
        const name = String(p?.name || '');
        const key = normalizeKey(name);

        const isCurrent = toBool(p?.isCurrentPlan, false);
        // A retired plan is never recommended, even if its row still says so.
        const isRecommended = p?.isActive !== false && toBool(p?.isRecommended, false);

        const monthlyPrice = toNumber(p?.monthlyPrice, 0);
        const annualPrice = toNumber(p?.annualPrice, 0);

        const monthlyCompareAtPrice =
          p?.monthlyCompareAtPrice != null ? toNumber(p?.monthlyCompareAtPrice, 0) : null;

        const annualCompareAtPrice =
          p?.annualCompareAtPrice != null ? toNumber(p?.annualCompareAtPrice, 0) : null;

        const hasMonthlyCompareAt =
          monthlyCompareAtPrice != null &&
          Number.isFinite(monthlyCompareAtPrice) &&
          monthlyCompareAtPrice > 0;

        const discountPeriod = String(p?.discount?.period || '')
          .trim()
          .toLowerCase();

        const isDiscounted =
          hasMonthlyCompareAt && monthlyPrice > 0 && monthlyPrice < monthlyCompareAtPrice;

        const hasAnnual = annualPrice > 0 && monthlyPrice > 0;
        const annualPerMonth = hasAnnual ? annualPrice / 12 : 0;
        const saveAmountMonthlyRaw = hasAnnual ? monthlyPrice - annualPrice / 12 : 0;
        const saveAmountMonthly = Math.round(saveAmountMonthlyRaw * 100) / 100;

        const saveText =
          hasAnnual && saveAmountMonthly > 0
            ? t('p.pricingPage.plan.saveOverMonthly', {
                defaultValue: 'Save ${{amount}} over monthly',
                amount: saveAmountMonthly,
              })
            : null;

        const billedText = hasAnnual
          ? t('p.pricingPage.plan.billedOnceYearly', { defaultValue: 'Billed once yearly' })
          : null;

        const extraPercentRaw =
          hasAnnual && annualPerMonth > 0
            ? ((monthlyPrice - annualPerMonth) / monthlyPrice) * 100
            : 0;

        const extraPercent = Math.round(extraPercentRaw);

        const extraText =
          hasAnnual && extraPercent >= 20
            ? t('p.pricingPage.plan.extraPercentOffYear', {
                defaultValue: 'Extra {{percent}}% off for 1 year(s)',
                percent: extraPercent,
              })
            : null;

        const isUnlimitedCampaigns = toBool(p?.isUnlimitedCampaigns, false);
        const isUnlimitedVariants = toBool(p?.isUnlimitedVariants, false);

        const maxCampaignsCount = toNumber(p?.maxCampaignsCount, 0);
        const maxVariantsCount = toNumber(p?.maxVariantsCount, 0);

        const metrics = [
          {
            value: formatLimit(isUnlimitedCampaigns, maxCampaignsCount, t),
            label: t('p.pricingPage.metrics.discountCampaigns', {
              defaultValue: 'Discount campaigns',
            }),
            iconKey: 'productList',
          },
          {
            value: formatLimit(isUnlimitedVariants, maxVariantsCount, t),
            label: t('p.pricingPage.metrics.liveDiscountedVariants', {
              defaultValue: 'Live discounted variants',
            }),
            iconKey: 'discount',
          },
        ];

        const featureSet = buildPlanFeatureIdSet(p);
        // Included features first, the rest after. Array.sort is stable, so each group keeps
        // the master order and every plan card lists its checkmarks on top.
        const featuresUi = masterFeatures
          .map((f) => ({
            id: f.id,
            featureId: f.id,
            key: `feature_${f.id}`,
            label: f.name,
            ok: featureSet.has(f.id),
            isComingSoon: f.isComingSoon === true,
          }))
          .sort((a, b) => Number(b.ok) - Number(a.ok));

        const perks = isUltimatePlan(p) ? buildUltimatePerks(p, t) : [];

        return {
          id: String(p?.id || ''),
          order: toNumber(p?.order, 0),
          key,

          name,
          subtitle: String(p?.description || ''),

          isCurrent,
          // Retired plan the shop is still on (only reachable as its current plan).
          isLegacy: p?.isActive === false,
          isRecommended,
          isDark: isRecommended,

          monthlyPrice,
          annualPrice,

          monthlyCompareAtPrice,
          annualCompareAtPrice,
          isDiscounted,
          discountPeriod,

          saveText,
          billedText,
          extraText,
          trialDays: Math.max(0, toNumber(p?.trialDays, 0)),
          // Raw campaign cap, so the plan card can say "Up to N campaigns" instead of
          // the vague "Limited feature access".
          maxCampaignsCount,
          isUnlimitedCampaigns,

          metrics,
          featuresUi,

          perks,

          raw: p,
        };
      })
      .filter((x) => x.id);

    mapped.sort((a, b) => toNumber(a.order, 0) - toNumber(b.order, 0));

    const firstGridIdx = mapped.findIndex((x) => String(x?.key || '') !== 'starter');
    if (firstGridIdx >= 0) {
      mapped[firstGridIdx] = { ...mapped[firstGridIdx], extraText: null };
    }

    return mapped;
  };
}
